// packages/web/src/process/edgeDecoration.ts
//
// Pure projection: WalkResult + activeStepIndex → EdgeDecoMap.
// No DOM, no React.
//
// Decorations key on the step's dense canonical `stepIndex` — the ONE contract
// the shared IR step-emitter guarantees between the graph's edges (each board
// CallDetail carries its emitting step's index) and the walker. Path-string
// keys are NOT joinable across the two: the walker sees the caller as written
// and the callee's INTERFACE path, while board details carry resolved
// module-path pairs (hoisting, nesting, instances, anonymous callees all
// diverge). The board resolves each of an edge's details by index and merges
// them with {@link aggregateEdgeDeco}; an index absent from the map means the
// step exists only on an unselected branch (the walk emits selected branches
// only) — the edge is on the process but off the selected path.

import type { NodeId, WalkNode, WalkResult, WalkStep } from "@archlang/engine";
import { buildDisplayNumbering } from "./displayNumbers.js";

export interface EdgeDeco {
  /** Canonical step indices, ascending. A single-step deco has exactly one. */
  readonly stepIndices: readonly number[];
  /**
   * Sequential 1-based positions in the selected run — the numbers the chip
   * SHOWS (canonical indices are internal identity only). Empty for a
   * compensation-only deco (chip shows ↩ instead).
   */
  readonly displayNumbers: readonly number[];
  readonly modifiers: {
    readonly loop: boolean;
    readonly parallel: boolean;
    readonly catch: boolean;
    /** Saga compensation step (a `reversible` item's `unwind` undo). */
    readonly compensation: boolean;
    /** Inside a retry loop's attempt (`each <n> try { … }`). */
    readonly retry: boolean;
    readonly branchLabels: readonly string[];
    readonly binds: readonly string[];
  };
  readonly state:
    | { kind: "idle" }
    | { kind: "active"; index: number }
    | { kind: "collapsed-group"; groupId: NodeId }
    | { kind: "parallel-group"; groupId: NodeId }
    | { kind: "all" }
    | { kind: "terminator"; type: "fail" | "finish"; reason?: string };
}

/** stepIndex (canonical, all-branches numbering) → that step's decoration. */
export type EdgeDecoMap = ReadonlyMap<number, EdgeDeco>;

interface TerminatorBadge {
  type: "fail" | "finish";
  reason?: string;
  /** stepIndex of the last preceding step at the same tree level. */
  precedingStepIndex: number;
}

function findTerminatorBadges(tree: readonly WalkNode[]): TerminatorBadge[] {
  const out: TerminatorBadge[] = [];
  function visit(nodes: readonly WalkNode[]): number | undefined {
    let lastStep: number | undefined;
    for (const n of nodes) {
      if (n.kind === "step") {
        lastStep = n.stepIndex;
        continue;
      }
      if (n.kind === "terminator") {
        if (lastStep !== undefined) {
          out.push({
            type: n.type,
            reason: n.reason,
            precedingStepIndex: lastStep,
          });
        }
        continue;
      }
      // Recurse into container kinds.
      if (n.kind === "each") visit(n.children);
      else if (n.kind === "if") {
        for (const b of n.branches) visit(b.children);
        if (n.elseBranch) visit(n.elseBranch.children);
      } else if (n.kind === "switch") {
        for (const c of n.cases) visit(c.children);
      } else if (n.kind === "try") {
        visit(n.tryBranch.children);
        for (const c of n.catches) visit(c.children);
      } else if (n.kind === "parallel") {
        for (const b of n.branches) visit(b.children);
      } else if (n.kind === "do" && n.body) {
        visit(n.body);
      }
    }
    return lastStep;
  }
  visit(tree);
  return out;
}

export function computeEdgeDecoration(
  walk: WalkResult,
  activeStepIndex: number | null,
): EdgeDecoMap {
  const out = new Map<number, EdgeDeco>();
  const terminatorBadges = findTerminatorBadges(walk.tree);
  const numbering = buildDisplayNumbering(walk);

  for (const step of walk.steps) {
    // Note / prose / go / await rows have no board edge; their indices still
    // exist in the numbering (the board never asks for them).
    if (step.noteText !== undefined) continue;
    const displayNumber = numbering.byIndex.get(step.stepIndex);
    out.set(step.stepIndex, {
      stepIndices: [step.stepIndex],
      displayNumbers: displayNumber !== undefined ? [displayNumber] : [],
      modifiers: {
        loop: step.modifiers.inEach,
        parallel: step.modifiers.inParallel,
        catch: step.modifiers.inCatch,
        compensation: step.modifiers.compensation === true,
        retry: step.modifiers.retry === true,
        branchLabels: step.modifiers.branchLabel ? [step.modifiers.branchLabel] : [],
        binds: step.bind ? [step.bind] : [],
      },
      state: computeState(walk, step, activeStepIndex, terminatorBadges),
    });
  }

  return out;
}

/**
 * Merge the decos a board edge's details resolved (one per detail stepIndex;
 * absent = unselected branch) into the single decoration the edge renders.
 * Undefined when nothing resolved — the edge carries only off-path steps.
 */
export function aggregateEdgeDeco(
  decos: readonly (EdgeDeco | undefined)[],
): EdgeDeco | undefined {
  const present = decos.filter((d): d is EdgeDeco => d !== undefined);
  if (present.length === 0) return undefined;
  if (present.length === 1) return present[0];

  const indices = [...new Set(present.flatMap((d) => d.stepIndices))].sort((a, b) => a - b);
  const displayNumbers = [...new Set(present.flatMap((d) => d.displayNumbers))].sort((a, b) => a - b);
  const branchLabels = unique(present.flatMap((d) => d.modifiers.branchLabels));
  const binds = unique(present.flatMap((d) => d.modifiers.binds));
  return {
    stepIndices: indices,
    displayNumbers,
    modifiers: {
      loop: present.some((d) => d.modifiers.loop),
      parallel: present.some((d) => d.modifiers.parallel),
      catch: present.some((d) => d.modifiers.catch),
      // Conservative: the chip COLORS on this flag, and a saga's forward call
      // and its own unwind usually share one board edge (same module pair) —
      // a mixed edge must not paint the forward step's number amber.
      compensation: present.every((d) => d.modifiers.compensation),
      retry: present.some((d) => d.modifiers.retry),
      branchLabels,
      binds,
    },
    state: present.map((d) => d.state).reduce(maxState),
  };
}

/**
 * The text a step chip renders for a decoration: sequential run numbers
 * (capped at 3, "…" beyond), `↩` for a compensation-only deco (failure
 * back-flow — not part of the numbered run), `·` when nothing is numbered.
 */
export function chipLabelFor(deco: EdgeDeco): string {
  if (deco.displayNumbers.length > 0) {
    const shown = deco.displayNumbers.slice(0, 3);
    return deco.displayNumbers.length > 3 ? `${shown.join(",")},…` : shown.join(",");
  }
  return deco.modifiers.compensation ? "↩" : "·";
}

/** State precedence for a multi-step edge: the most "urgent" step wins. */
const STATE_RANK: Record<EdgeDeco["state"]["kind"], number> = {
  idle: 0,
  all: 1,
  "collapsed-group": 2,
  "parallel-group": 3,
  active: 4,
  terminator: 5,
};

function maxState(a: EdgeDeco["state"], b: EdgeDeco["state"]): EdgeDeco["state"] {
  return STATE_RANK[b.kind] > STATE_RANK[a.kind] ? b : a;
}

function unique(xs: readonly string[]): string[] {
  return [...new Set(xs)];
}

function computeState(
  walk: WalkResult,
  step: WalkStep,
  activeStepIndex: number | null,
  terminatorBadges: readonly TerminatorBadge[],
): EdgeDeco["state"] {
  if (activeStepIndex === null) return { kind: "all" };

  // Terminator badge takes PRECEDENCE over group highlight: if the active step is
  // this step and a `fail`/`finish` immediately follows it, show the badge
  // — even when the step is inside a parallel branch or a collapsed `do` (the
  // group state must not mask the terminator, e.g. `fail` ending a parallel race
  // branch in saga.arch).
  if (step.stepIndex === activeStepIndex) {
    const badge = terminatorBadges.find((b) => b.precedingStepIndex === activeStepIndex);
    if (badge) return { kind: "terminator", type: badge.type, reason: badge.reason };
  }

  // Collapsed-group: if the active index is inside a collapsed group AND
  // this step shares that group, decorate as collapsed-group.
  for (const [groupId, range] of walk.collapsedGroups) {
    if (
      activeStepIndex >= range.startIndex &&
      activeStepIndex <= range.endIndex &&
      step.modifiers.collapsedSubprocessGroup === groupId
    ) {
      return { kind: "collapsed-group", groupId };
    }
  }

  // Parallel-group: same idea for parallel.
  for (const [groupId, range] of walk.parallelGroups) {
    if (
      activeStepIndex >= range.startIndex &&
      activeStepIndex <= range.endIndex &&
      step.modifiers.parallelGroup === groupId
    ) {
      return { kind: "parallel-group", groupId };
    }
  }

  // Active (non-terminator, non-group): this step is the active one.
  if (step.stepIndex === activeStepIndex) {
    return { kind: "active", index: activeStepIndex };
  }
  return { kind: "idle" };
}
